import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { apiClient, call, callVoid } from "../shared/api/client";
import { problemMessage } from "../shared/api/problem";
import type { TestModeUserOption } from "../shared/api/types";
import { outsideOf, useDismissLayer } from "../shared/lib/dismissLayers";
import { useDebouncedValue } from "../shared/lib/useDebouncedValue";
import { testModeSwitchKey } from "../shared/session/queries";
import { useSession } from "../shared/session/session-context";
import { useTestModeHeartbeat } from "./testModeActivity";
import { useCrewLabels } from "../shared/crew/useCrewLabels";
import { OFF, OTHER_PROJECT, USER, VIEWED_USER, roleProjectFor, testModeSentence, testModeValue } from "./testModeText";

/**
 * Test mode (Features__RoleTestMode, contract `PUT/DELETE /auth/test-mode`): a SystemAdmin acts as one project role on
 * the current project, or views as another person, to check what they can see and do. The server enforces it; this is
 * only the switch and the reminder. Shown only when `Me.testMode` is present (a SystemAdmin, flag on; on production only
 * when set explicitly there, 0.10.58). On production the strip says so in the error colours, and wherever an idle limit
 * applies the strip sends the heartbeat while the admin uses the page (testModeActivity.ts).
 *
 * Rule shown to the user: role mode applies to ONE project. On any other project you have your own roles; choosing a
 * role there moves test mode to that project. User mode applies everywhere. While viewing as a user, a "Role on" project
 * select lists every project the real admin sees (`TestMode.projects`), since the header's list is the viewed person's.
 */

function useTestModeSwitch() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // A mutation (not local state) so the whole switch, refetch included, is visible app-wide under testModeSwitchKey.
  const switching = useMutation({
    mutationKey: testModeSwitchKey,
    mutationFn: async (action: () => Promise<unknown>) => {
      await action();
      // Every cached answer was computed for the previous identity: refetch all of it, the session first.
      await queryClient.invalidateQueries();
    },
  });

  async function run(action: () => Promise<unknown>): Promise<boolean> {
    setError(null);
    try {
      await switching.mutateAsync(action);
      return true;
    } catch (failure) {
      setError(problemMessage(failure));
      return false;
    }
  }

  return {
    error,
    pending: switching.isPending,
    actAs: (projectKey: string, role: string) => run(() => call(apiClient.PUT("/auth/test-mode", { body: { projectKey, role: role as never } }))),
    viewAs: (asUserId: number) => run(() => call(apiClient.PUT("/auth/test-mode", { body: { asUserId } }))),
    turnOff: () => run(() => callVoid(apiClient.DELETE("/auth/test-mode"))),
  };
}

function UserPicker({ onPick, onCancel, pending }: { onPick: (user: TestModeUserOption) => void; onCancel: () => void; pending: boolean }) {
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState<TestModeUserOption | null>(null);
  const debounced = useDebouncedValue(q.trim(), 250);
  const inputId = useId();
  const listId = useId();
  const people = useQuery({
    queryKey: ["test-mode-users", debounced],
    queryFn: async () => (await call(apiClient.GET("/auth/test-mode/users", { params: { query: { q: debounced || undefined, limit: 20 } } }))).items,
  });

  return (
    <div className="gf-testmode-picker" role="group" aria-label="View as a user">
      <label htmlFor={inputId}>Find a user</label>
      <input
        id={inputId}
        type="search"
        value={q}
        autoComplete="off"
        placeholder="Name or email"
        aria-controls={listId}
        onChange={(event) => {
          setQ(event.target.value);
          setChosen(null);
        }}
      />
      <ul id={listId} className="gf-testmode-results" aria-label="Matching users">
        {people.isPending && <li className="help">Loading…</li>}
        {people.isError && (
          <li className="msg" role="alert">
            {problemMessage(people.error)}
          </li>
        )}
        {people.isSuccess && people.data.length === 0 && <li className="help">No active user matches. System Admins are never listed.</li>}
        {people.data?.map((person) => (
          <li key={person.id}>
            <label className="gf-testmode-option">
              <input type="radio" name="gf-testmode-user" checked={chosen?.id === person.id} onChange={() => setChosen(person)} />
              <span>
                {person.displayName}
                {person.userKind === "partner" && <span className="gf-partner-chip small">Partner</span>}
                {/* Batch 19 S8c: offered only while the portal is on; the preview reads and never writes. */}
                {person.userKind === "external" && <span className="help"> External tester: portal preview, read only</span>}
                {person.email && <span className="help"> {person.email}</span>}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className="gf-testmode-actions">
        <button type="button" className="btn" disabled={!chosen || pending} onClick={() => chosen && onPick(chosen)}>
          View as
        </button>
        <button type="button" className="btn sec" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Header control, right of the top bar next to the user menu: "View as" select plus the user picker. */
export function TestModeControl({ projectKey }: { projectKey: string }) {
  const crew = useCrewLabels();
  const roleWords = crew.pickerLabel;
  const session = useSession();
  const testMode = session.testMode;
  const switcher = useTestModeSwitch();
  const [picking, setPicking] = useState(false);
  const [pickedProject, setPickedProject] = useState<string | null>(null);
  const selectId = useId();
  const projectSelectId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  // The user picker is a floating panel: Esc or a click outside closes it like any other layer (ruling 2026-10-01).
  // A search query or a highlighted person is a lookup, not typed work, so it closes without asking.
  useDismissLayer(picking && Boolean(testMode), {
    element: () => rootRef.current,
    isOutside: (event) => outsideOf(event, rootRef.current),
    dismiss: (reason) => {
      setPicking(false);
      if (reason === "escape") document.getElementById(selectId)?.focus();
    },
  });
  if (!testMode) return null;

  const value = picking ? USER : testModeValue(testMode, projectKey);
  // While viewing as a user, "Role on" chooses from every project the real admin sees, not the viewed person's (R7-D01).
  const viewingAsUser = testMode.active && testMode.kind === "user";
  const roleProject = roleProjectFor(testMode, projectKey, pickedProject);

  async function onChange(next: string) {
    if (next === USER) {
      setPicking(true);
      return;
    }
    setPicking(false);
    if (next === OFF) await switcher.turnOff();
    else if (next.startsWith("role:")) await switcher.actAs(roleProject, next.slice("role:".length));
  }

  const roleGroups = crew.splitRoles(testMode.roles);

  return (
    <div className="gf-testmode" ref={rootRef} data-guide="test-mode">
      <label htmlFor={selectId}>View as</label>
      <select id={selectId} value={value} disabled={switcher.pending} onChange={(event) => void onChange(event.target.value)}>
        <option value={OFF}>Off (my real roles)</option>
        {testMode.active && testMode.kind === "role" && testMode.projectKey !== projectKey && (
          <option value={OTHER_PROJECT} disabled>
            {roleWords(testMode.role ?? "")} on {testMode.projectKey}
          </option>
        )}
        {testMode.active && testMode.kind === "user" && (
          <option value={VIEWED_USER} disabled>
            {testMode.asUser?.displayName}
          </option>
        )}
        <optgroup label={roleProject ? `Role on ${roleProject}` : "Role (choose a project first)"}>
          {/* D-B14-01: crew rank order (Chief Pilot, Capt., FO, Cabin Crew, Passenger), whatever order the server sent. */}
          {roleGroups.crew.map((role) => (
            <option key={role} value={`role:${role}`} disabled={!roleProject}>
              {roleWords(role)}
            </option>
          ))}
        </optgroup>
        {/* Revision 107: the ATC group has no rank, so it is listed apart, after the flight crew. */}
        {roleGroups.ground.length > 0 && (
          <optgroup label={roleProject ? `ATC on ${roleProject}` : "ATC (choose a project first)"}>
            {roleGroups.ground.map((role) => (
              <option key={role} value={`role:${role}`} disabled={!roleProject}>
                {roleWords(role)}
              </option>
            ))}
          </optgroup>
        )}
        <option value={USER}>A user…</option>
      </select>
      {viewingAsUser && testMode.projects.length > 0 && (
        <>
          <label htmlFor={projectSelectId}>Role on</label>
          <select
            id={projectSelectId}
            value={roleProject}
            disabled={switcher.pending}
            onChange={(event) => setPickedProject(event.target.value)}
          >
            {testMode.projects.map((project) => (
              <option key={project.key} value={project.key}>
                {project.key} · {project.name}
              </option>
            ))}
          </select>
        </>
      )}
      {picking && (
        <div className="gf-testmode-panel">
          <UserPicker
            pending={switcher.pending}
            onCancel={() => setPicking(false)}
            onPick={(user) =>
              void switcher.viewAs(user.id).then((ok) => {
                if (ok) setPicking(false);
              })
            }
          />
        </div>
      )}
      {switcher.error && (
        <div className="gf-testmode-error msg" role="alert">
          {switcher.error}
        </div>
      )}
    </div>
  );
}

/** The coloured strip under the top bar while test mode is on; red and "Production —" on production. */
export function TestModeStrip({ projectKey }: { projectKey: string }) {
  const session = useSession();
  const crew = useCrewLabels();
  const queryClient = useQueryClient();
  const switcher = useTestModeSwitch();
  const testMode = session.testMode;
  useTestModeHeartbeat({
    active: Boolean(testMode?.active),
    idleMinutes: testMode?.idleMinutes,
    send: async () => (await call(apiClient.POST("/auth/test-mode/activity"))).active,
    // Test mode may have ended: every cached answer may belong to the test identity, so refetch all of it.
    recheck: () => void queryClient.invalidateQueries(),
  });
  const sentence = testModeSentence(testMode, projectKey, projectKey ? session.rolesIn(projectKey) : [], crew.pickerLabel);
  if (!sentence) return null;
  const production = testMode?.production === true;
  return (
    <div
      className={production ? "gf-testmode-strip prod" : "gf-testmode-strip"}
      role="status"
      aria-live="polite"
      aria-label={production ? "Test mode on production" : "Test mode"}
    >
      <span>{sentence}</span>
      <span aria-hidden="true"> · </span>
      <button type="button" className="linkish" disabled={switcher.pending} onClick={() => void switcher.turnOff()}>
        Turn off
      </button>
      {switcher.error && <span role="alert"> {switcher.error}</span>}
    </div>
  );
}
